import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import AddEditCardModal from "./AddEditCardModal";
import CardConsumptionModal from "./CardConsumptionModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import Pagination from "../../../components/Pagination/Pagination";
import "./CashlessPage.scss";
import deleteIcon from "../../../images/DeleteIcon.svg";
import editIcon from "../../../images/EditIcon.svg";
import invoiceIcon from "../../../images/invoice.svg";

function CashlessIcon({ kind, ...props }) {
  const paths = {
    cards: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="M3 10h18M7 15h4" />
      </>
    ),
    cash: (
      <>
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="3" />
        <path d="M6 12h.01M18 12h.01" />
      </>
    ),
    reload: (
      <>
        <path d="M20 7v5h-5M4 17v-5h5" />
        <path d="M6 7a7 7 0 0 1 12-1l2 3M4 15l2 3a7 7 0 0 0 12-1" />
      </>
    ),
    qr: (
      <>
        <path d="M3 3h6v6H3zM15 3h6v6h-6zM3 15h6v6H3zM15 15h2v2h-2zM21 14v4h-3v3M14 21h1" />
      </>
    ),
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),
    balance: (
      <>
        <path d="M19 5H7a3 3 0 0 0 0 6h14v9H7a3 3 0 0 1-3-3V8M21 11V7H7M16 15h2" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {paths[kind] || paths.cards}
    </svg>
  );
}

export function CashlessPage({ organizations, orgId }) {
  const { t } = useTranslation("cashless");
  const alert = useAlert();

  const selectedOrg = organizations?.find((org) => orgId?.includes(org.id));
  const selectedOrgUid = selectedOrg?.uid;

  const [cashlessCards, setCashlessCards] = useState([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cardsLoadError, setCardsLoadError] = useState(false);
  const [cardsRefreshKey, setCardsRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConsumptionModal, setShowConsumptionModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activationCost, setActivationCost] = useState(selectedOrg?.cashlessActivationCost || 0);
  const [isSavingActivationCost, setIsSavingActivationCost] = useState(false);
  const [savingSettings, setSavingSettings] = useState({});
  const pendingSettings = useRef(new Set());
  const pendingActivationCost = useRef(false);
  const itemsPerPage = 20;

  useEffect(() => {
    setCardsLoadError(false);
    if (!selectedOrgUid) {
      setCashlessCards([]);
      setTotalBalance(0);
      setLoading(false);
      return;
    }

    setCurrentPage(1);
    setCashlessCards([]);
    setTotalBalance(0);
    setLoading(true);
    const q = query(collection(db, "Cashless"), where("creatorId", "==", selectedOrgUid));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const cards = [];
        let totalBalance = 0;
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          cards.push({
            id: data.id || doc.id,
            balance: data.currentAmount || 0,
            holder: data.holder || "",
            createdAt: Number(data.createdAt) || 0,
          });
          totalBalance += data.currentAmount || 0;
        });
        setCashlessCards(cards.sort((first, second) => second.createdAt - first.createdAt));
        setTotalBalance(totalBalance);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching cashless cards:", error);
        setCardsLoadError(true);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [selectedOrgUid, cardsRefreshKey]);

  useEffect(() => {
    setActivationCost(selectedOrg?.cashlessActivationCost ?? 0);
  }, [selectedOrg?.id, selectedOrg?.cashlessActivationCost]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const filteredCards = cashlessCards.filter((card) =>
    card.id.trim().toLowerCase().includes(searchTerm.trim().toLowerCase()),
  );
  const totalPages = Math.ceil(filteredCards.length / itemsPerPage);
  const visiblePage = Math.min(currentPage, Math.max(1, totalPages));
  const startIndex = (visiblePage - 1) * itemsPerPage;
  const paginatedCards = filteredCards.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddCard = () => {
    setSelectedCard(null);
    setShowAddEditModal(true);
  };

  const handleEditCard = (card) => {
    setSelectedCard(card);
    setShowAddEditModal(true);
  };

  const handleCloseModal = () => {
    setShowAddEditModal(false);
    setSelectedCard(null);
  };

  const handleSaveCard = async (cardData) => {
    if (!selectedOrgUid) return;

    try {
      const cardRef = doc(db, "Cashless", cardData.id);
      if (!selectedCard) {
        const existingCard = await getDoc(cardRef);
        if (existingCard.exists()) {
          const creatorId = existingCard.data()?.creatorId;
          let ownerOrganizations = (organizations || []).filter((org) => creatorId && org.uid === creatorId);
          if (creatorId && !ownerOrganizations.length) {
            try {
              const owners = await getDocs(query(collection(db, "Organizations"), where("uid", "==", creatorId)));
              ownerOrganizations = owners.docs.map((org) => org.data());
            } catch (lookupError) {
              // A denied/unavailable name lookup must never permit overwriting the card.
              ownerOrganizations = [];
            }
          }
          const names = [...new Set(ownerOrganizations.map((org) => org.organization).filter(Boolean))];
          const message = names.length
            ? t("cardAlreadyExistsInOrganizations", { organizations: names.join(", ") })
            : creatorId
              ? t("cardAlreadyExistsForCreator", { creatorId })
              : t("cardAlreadyExistsUnknownOwner");
          throw Object.assign(new Error(message), { code: "cashless-card-id-already-exists" });
        }
      }
      await setDoc(
        cardRef,
        {
          id: cardData.id,
          currentAmount: cardData.balance,
          creatorId: selectedOrgUid,
          holder: cardData.holder,
          ...(!selectedCard ? { createdAt: Date.now() } : {}),
        },
        { merge: Boolean(selectedCard) },
      );
      alert.show(t("cardsUpdatedSuccess"), { type: "success" });
    } catch (error) {
      const duplicate = error.code === "cashless-card-id-already-exists";
      alert.show(duplicate ? error.message : t("cardsUpdateError"), { type: "error" });
      if (!duplicate) console.error("Error saving card:", error);
      throw error;
    }
  };

  const handleDeleteCard = (card) => {
    setSelectedCard(card);
    setShowDeleteModal(true);
  };

  const handleShowConsumption = (card) => {
    setSelectedCard(card);
    setShowConsumptionModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedCard(null);
  };

  const handleCloseConsumptionModal = () => {
    setShowConsumptionModal(false);
    setSelectedCard(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCard || !selectedOrg?.id) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "Cashless", selectedCard.id));
      handleCloseDeleteModal();
      alert.show(t("cardDeletedSuccess"), { type: "success" });
    } catch (error) {
      console.error("Error deleting card:", error);
      alert.show(t("cardDeleteError"), { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveActivationCost = async () => {
    if (!selectedOrg?.id || pendingActivationCost.current) return;

    pendingActivationCost.current = true;
    setIsSavingActivationCost(true);
    try {
      await updateDoc(doc(db, "Organizations", selectedOrg.id), {
        cashlessActivationCost: parseFloat(activationCost) || 0,
      });
      alert.show(t("activationCostUpdatedSuccess"), { type: "success" });
    } catch (error) {
      console.error("Error saving activation cost:", error);
      alert.show(t("activationCostUpdateError"), { type: "error" });
    } finally {
      pendingActivationCost.current = false;
      setIsSavingActivationCost(false);
    }
  };

  const formatBalance = (balance) => {
    return `${balance.toFixed(2)} €`;
  };

  const updateSetting = async (field, value, errorKey) => {
    if (!selectedOrg?.id) return;
    const requestKey = `${selectedOrg.id}:${field}`;
    if (pendingSettings.current.has(requestKey)) return;
    pendingSettings.current.add(requestKey);
    setSavingSettings((current) => ({ ...current, [requestKey]: true }));
    try {
      await updateDoc(doc(db, "Organizations", selectedOrg.id), { [field]: value });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      alert.show(t(errorKey), { type: "error" });
    } finally {
      pendingSettings.current.delete(requestKey);
      setSavingSettings((current) => ({ ...current, [requestKey]: false }));
    }
  };

  const settings = [
    {
      field: "cashRefused",
      icon: "cash",
      title: "cashPayments",
      hint: "block_cash_option",
      error: "cashSettingsUpdateError",
      options: ["allowed", "refused"],
    },
    {
      field: "cashlessRefused",
      icon: "cards",
      title: "cashlessPayments",
      hint: "block_cashless_option",
      error: "cashlessSettingsUpdateError",
      options: ["allowed", "refused"],
    },
    {
      field: "cashlessActivated",
      icon: "reload",
      title: "cashlessReload",
      hint: "info_cashless_reload",
      error: "cashlessReloadSettingsUpdateError",
    },
    {
      field: "acceptQrCashless",
      icon: "qr",
      title: "cashlessQr",
      hint: "acceptCashlessQrHint",
      error: "cashlessQrSettingsUpdateError",
    },
  ];

  return (
    <div className="container-fluid cashless-page">
      <header className="cashless-page-header">
        <div>
          <h1>{t("cashlessCardsList")}</h1>
          <p>{t("pageDescription")}</p>
        </div>
        <button
          type="button"
          className="cashless-primary-btn"
          onClick={handleAddCard}
          disabled={!selectedOrgUid}
        >
          + {t("addCard")}
        </button>
      </header>

      <div className="cashless-summary" aria-busy={loading}>
        <div className="cashless-stat">
          <span className="cashless-icon">
            <CashlessIcon kind="cards" />
          </span>
          <div>
            <span className="cashless-stat-label">{t("cardsCount")}</span>
            <strong>{loading || cardsLoadError ? "—" : cashlessCards.length}</strong>
          </div>
        </div>
        <div className="cashless-stat">
          <span className="cashless-icon">
            <CashlessIcon kind="balance" />
          </span>
          <div>
            <span className="cashless-stat-label">{t("totalBalance")}</span>
            <strong>{loading || cardsLoadError ? "—" : formatBalance(totalBalance)}</strong>
          </div>
        </div>
      </div>

      <section
        className="cashless-panel cashless-settings"
        aria-labelledby="cashless-settings-title"
      >
        <div className="cashless-section-heading">
          <h2 id="cashless-settings-title">{t("cashlessSettings")}</h2>
          <p>{t("settingsDescription")}</p>
        </div>
        <div className="cashless-settings-grid">
          {settings.map((setting) => {
            const enabled = Boolean(selectedOrg?.[setting.field]);
            const saving = Boolean(savingSettings[`${selectedOrg?.id}:${setting.field}`]);
            const disabled = !selectedOrg?.id || saving;
            const titleId = `cashless-${setting.field}-title`;
            const hintId = `cashless-${setting.field}-hint`;
            return (
              <div className="cashless-setting" key={setting.field} aria-busy={saving}>
                <div className="cashless-setting-heading">
                  <span className="cashless-icon">
                    <CashlessIcon kind={setting.icon} />
                  </span>
                  <h3 id={titleId}>{t(setting.title)}</h3>
                  <span className="cashless-saving" role="status">
                    {saving ? t("saving") : ""}
                  </span>
                </div>
                <p id={hintId}>{t(setting.hint)}</p>
                {setting.options ? (
                  <div
                    className="cashless-segmented"
                    role="group"
                    aria-labelledby={titleId}
                    aria-describedby={hintId}
                  >
                    {setting.options.map((option, index) => (
                      <button
                        type="button"
                        key={option}
                        aria-pressed={enabled === Boolean(index)}
                        disabled={disabled}
                        onClick={() => updateSetting(setting.field, Boolean(index), setting.error)}
                      >
                        {t(option)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="cashless-toggle-row">
                    <span className={enabled ? "cashless-enabled" : ""}>
                      {t(enabled ? "enabled" : "disabled")}
                    </span>
                    <button
                      type="button"
                      className="cashless-switch"
                      role="switch"
                      aria-checked={enabled}
                      aria-labelledby={titleId}
                      aria-describedby={hintId}
                      disabled={disabled}
                      onClick={() => updateSetting(setting.field, !enabled, setting.error)}
                    >
                      <span />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <form
          className="cashless-activation"
          onSubmit={(event) => {
            event.preventDefault();
            handleSaveActivationCost();
          }}
        >
          <div>
            <label htmlFor="activationCost">{t("activationCost")}</label>
            <p id="activation-cost-hint">{t("activationCostHint")}</p>
          </div>
          <div className="cashless-activation-controls">
            <div className="cashless-currency-input">
              <input
                id="activationCost"
                type="number"
                min="0"
                step="0.01"
                value={activationCost}
                disabled={!selectedOrg?.id || isSavingActivationCost}
                aria-describedby="activation-cost-hint"
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "" || (!isNaN(value) && parseFloat(value) >= 0))
                    setActivationCost(value);
                }}
                onKeyDown={(event) => {
                  if (["-", "e", "E", "+"].includes(event.key)) event.preventDefault();
                }}
                placeholder="0.00"
              />
              <span aria-hidden="true">€</span>
            </div>
            <button
              type="submit"
              className="cashless-secondary-btn"
              disabled={!selectedOrg?.id || isSavingActivationCost}
            >
              {isSavingActivationCost ? t("saving") : t("save")}
            </button>
          </div>
        </form>
      </section>

      <section className="cashless-panel cashless-cards" aria-labelledby="cashless-list-title">
        <div className="cashless-list-toolbar">
          <div>
            <h2 id="cashless-list-title">{t("cardsManagement")}</h2>
            <p role="status">
              {loading
                ? t("loading")
                : cardsLoadError
                  ? t("cardsLoadError")
                  : t("resultsCount", { count: filteredCards.length })}
            </p>
          </div>
          <div className="cashless-search">
            <CashlessIcon kind="search" />
            <input
              type="search"
              placeholder={t("searchPlaceholder")}
              aria-label={t("search")}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>
        {loading ? (
          <div className="cashless-empty" role="status">
            <span className="cashless-loader" aria-hidden="true" />
            <p>{t("loading")}</p>
          </div>
        ) : cardsLoadError ? (
          <div className="cashless-empty" role="alert">
            <h3>{t("cardsLoadError")}</h3>
            <button
              type="button"
              className="cashless-secondary-btn"
              onClick={() => setCardsRefreshKey((value) => value + 1)}
            >
              {t("retry")}
            </button>
          </div>
        ) : filteredCards.length === 0 ? (
          <div className="cashless-empty">
            <span className="cashless-icon">
              <CashlessIcon kind={searchTerm ? "search" : "cards"} />
            </span>
            <h3>{searchTerm ? t("noCardsFound") : t("noCards")}</h3>
            <p>{searchTerm ? t("noResultsHint") : t("emptyCardsHint")}</p>
            {searchTerm ? (
              <button
                type="button"
                className="cashless-secondary-btn"
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
              >
                {t("clearSearch")}
              </button>
            ) : (
              <button
                type="button"
                className="cashless-secondary-btn"
                onClick={handleAddCard}
                disabled={!selectedOrgUid}
              >
                {t("addCard")}
              </button>
            )}
          </div>
        ) : (
          <>
            <div
              className="cashless-table-scroll"
              role="region"
              aria-labelledby="cashless-list-title"
              tabIndex={0}
            >
              <table className="cashless-table">
                <thead>
                  <tr>
                    <th scope="col">{t("cardNumber")}</th>
                    <th scope="col">{t("holder")}</th>
                    <th scope="col" className="cashless-balance-cell">
                      {t("balance")}
                    </th>
                    <th scope="col">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCards.map((card) => (
                    <tr key={card.id}>
                      <td>
                        <code>{card.id}</code>
                      </td>
                      <td>{card.holder || <span className="cashless-muted">—</span>}</td>
                      <td className="cashless-balance-cell">{formatBalance(card.balance)}</td>
                      <td>
                        <div className="cashless-row-actions">
                          <button
                            type="button"
                            title={t("viewConsumption")}
                            aria-label={t("viewConsumption")}
                            onClick={() => handleShowConsumption(card)}
                          >
                            <img src={invoiceIcon} alt="" />
                          </button>
                          <button
                            type="button"
                            title={t("editCard")}
                            aria-label={t("editCard")}
                            onClick={() => handleEditCard(card)}
                          >
                            <img src={editIcon} alt="" />
                          </button>
                          <button
                            type="button"
                            className="cashless-delete-action"
                            title={t("deleteCard")}
                            aria-label={t("deleteCard")}
                            onClick={() => handleDeleteCard(card)}
                          >
                            <img src={deleteIcon} alt="" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cashless-list-footer">
              <span>
                {t("cardsRange", {
                  start: startIndex + 1,
                  end: Math.min(startIndex + itemsPerPage, filteredCards.length),
                  count: filteredCards.length,
                })}
              </span>
              <nav aria-label={t("cardsPagination")}>
                <Pagination
                  currentPage={visiblePage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </nav>
            </div>
          </>
        )}
      </section>
      <AddEditCardModal
        show={showAddEditModal}
        onHide={handleCloseModal}
        onSave={handleSaveCard}
        cardToEdit={selectedCard}
      />
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        cardToDelete={selectedCard}
        loading={loading}
      />
      <CardConsumptionModal
        show={showConsumptionModal}
        onHide={handleCloseConsumptionModal}
        card={selectedCard}
        orgId={selectedOrg?.id}
      />
    </div>
  );
}

export default CashlessPage;
